import { useState } from 'react';
import { Button, IconButton, Stack, TextField, Tooltip, MenuItem, Snackbar, Alert } from '@mui/material';
import { Add, Edit, Delete, Description, Place, FactCheck, Handshake, UploadFile, Group } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../../components/PageHeader';
import { DataTable, type Column } from '../../../components/DataTable';
import { StatusBadge } from '../../../components/StatusBadge';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { BulkImportDialog } from '../../../components/BulkImportDialog';
import { ProfileStatusChip } from '../../../components/ProfileStatusChip';
import { PortalUsersDialog } from '../../../components/PortalUsersDialog';
import { bulkOnboardApi } from '../../../api/bulkOnboard.api';
import { DealerFormDialog } from './DealerFormDialog';
import { DealerDocumentsDialog } from './DealerDocumentsDialog';
import { DealerAddressesDialog } from './DealerAddressesDialog';
import { DealerDetailDialog } from './DealerDetailDialog';
import { dealersApi, type Dealer, type CreateDealerInput, type DealerStatus } from '../../../api/dealers.api';
import { extractApiError } from '../../../api/error';
import { useAuth } from '../../../hooks/useAuth';

const STATUSES: DealerStatus[] = ['draft', 'pending_approval', 'approved', 'rejected', 'suspended', 'inactive'];
const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;

export function DealerListPage() {
  const qc = useQueryClient();
  const { hasPermission, user } = useAuth();
  const isAdmin = (user?.roles ?? []).some((r) => r === 'super_admin' || r === 'admin');
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Dealer | null>(null);
  const [deleting, setDeleting] = useState<Dealer | null>(null);
  const [docsDealer, setDocsDealer] = useState<Dealer | null>(null);
  const [addrDealer, setAddrDealer] = useState<Dealer | null>(null);
  const [detail, setDetail] = useState<Dealer | null>(null);
  const [usersDealer, setUsersDealer] = useState<Dealer | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['dealers', page, limit, search, status],
    queryFn: () => dealersApi.list({ page, limit, search: search || undefined, status: status || undefined }),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['dealers'] });

  const createMut = useMutation({
    mutationFn: (input: CreateDealerInput) => dealersApi.create(input),
    onSuccess: () => { setFormOpen(false); invalidate(); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, input }: { id: number; input: CreateDealerInput }) => dealersApi.update(id, input),
    onSuccess: () => { setFormOpen(false); setEditing(null); invalidate(); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => dealersApi.remove(id),
    onSuccess: () => { setDeleting(null); invalidate(); },
    onError: (e) => { setToast(extractApiError(e)); setDeleting(null); },
  });

  // Keep the open detail dialog in sync after a workflow action mutates the row.
  const syncDetail = () => {
    invalidate();
    if (detail) {
      dealersApi.get(detail.id).then(setDetail).catch(() => undefined);
    }
  };

  const columns: Column<Dealer>[] = [
    { key: 'dealerCode', label: 'Code', width: 120 },
    { key: 'dealerName', label: 'Dealer' },
    { key: 'dealerType', label: 'Type' },
    { key: 'creditLimit', label: 'Credit Limit', align: 'right', render: (r) => inr(r.creditLimit) },
    { key: 'profileStatus', label: 'Profile', render: (r) => <ProfileStatusChip status={r.profileStatus} /> },
    { key: 'hasLogin', label: 'Login', render: (r) => (r.hasLogin ? 'Yes' : '—') },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <>
      <PageHeader
        title="Dealers"
        subtitle="EVCPD dealer onboarding, approval and self-service access."
        action={
          hasPermission('dealer.create') && (
            <Stack direction="row" spacing={1}>
              {hasPermission('dealer_application.read') && (
                <Button startIcon={<Handshake />} variant="outlined" onClick={() => navigate('/pdi/dealer-applications')}>
                  Full Onboarding
                </Button>
              )}
              <Button startIcon={<UploadFile />} variant="outlined" onClick={() => setBulkOpen(true)}>
                Bulk Upload
              </Button>
              <Button startIcon={<Add />} variant="contained" onClick={() => { setEditing(null); setFormError(null); setFormOpen(true); }}>
                Quick Register
              </Button>
            </Stack>
          )
        }
      />
      <Stack direction="row" spacing={2} mb={2}>
        <TextField size="small" placeholder="Search name / code / GST" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        <TextField select size="small" label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} sx={{ minWidth: 180 }}>
          <MenuItem value="">All</MenuItem>
          {STATUSES.map((s) => <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>)}
        </TextField>
      </Stack>
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        loading={isLoading}
        page={page}
        limit={limit}
        total={data?.meta.total ?? 0}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
        rowKey={(r) => r.id}
        actions={(r) => (
          <>
            <Tooltip title="Workflow & details"><IconButton size="small" onClick={() => setDetail(r)}><FactCheck fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Documents"><IconButton size="small" onClick={() => setDocsDealer(r)}><Description fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Addresses"><IconButton size="small" onClick={() => setAddrDealer(r)}><Place fontSize="small" /></IconButton></Tooltip>
            {hasPermission('dealer.update') && (
              <Tooltip title="Users / Team"><IconButton size="small" onClick={() => setUsersDealer(r)}><Group fontSize="small" /></IconButton></Tooltip>
            )}
            {hasPermission('dealer.update') && (r.status !== 'approved' || isAdmin) && (
              <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditing(r); setFormError(null); setFormOpen(true); }}><Edit fontSize="small" /></IconButton></Tooltip>
            )}
            {hasPermission('dealer.delete') && (r.status !== 'approved' || isAdmin) && (
              <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>
            )}
          </>
        )}
      />

      <DealerFormDialog
        open={formOpen}
        initial={editing}
        error={formError}
        onClose={() => setFormOpen(false)}
        onSubmit={async (input) => {
          setFormError(null);
          if (editing) await updateMut.mutateAsync({ id: editing.id, input });
          else await createMut.mutateAsync(input);
        }}
      />
      <BulkImportDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        title="Bulk upload dealers"
        entityLabel="Dealer"
        columns={[
          { header: 'Business vertical', sample: 'Protection Device Installation' },
          { header: 'Client Name', sample: 'Acme Power Pvt Ltd' },
          { header: 'Dealer Name', sample: 'Sunrise EV Motors', required: true },
          { header: 'Owner Name', sample: 'Amit Shah' },
          { header: 'Address', sample: '12 MG Road' },
          { header: 'Pincode', sample: '411001' },
          { header: 'City', sample: 'Pune' },
          { header: 'State', sample: 'Maharashtra' },
          { header: 'Region', sample: 'West' },
          { header: 'Contact Number', sample: '9876543210' },
          { header: 'Login Email Id', sample: 'amit@sunriseev.com' },
          { header: 'Credit Limit', sample: '50000' },
          { header: 'Credit Days', sample: '30' },
          { header: 'GST Number', sample: '27AAAAA0000A1Z5' },
          { header: 'PAN Number', sample: 'AAAAA0000A' },
        ]}
        onImport={(rows, createLogins) => bulkOnboardApi.dealers(rows, createLogins)}
        onDone={invalidate}
      />
      <PortalUsersDialog open={Boolean(usersDealer)} resource="dealers" entityId={usersDealer?.id ?? null} entityName={usersDealer?.dealerName} onClose={() => setUsersDealer(null)} />
      <DealerDocumentsDialog dealerId={docsDealer?.id ?? null} dealerName={docsDealer?.dealerName} onClose={() => setDocsDealer(null)} />
      <DealerAddressesDialog dealerId={addrDealer?.id ?? null} dealerName={addrDealer?.dealerName} onClose={() => setAddrDealer(null)} />
      <DealerDetailDialog dealer={detail} onClose={() => setDetail(null)} onChanged={syncDetail} />
      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete dealer"
        message={`Soft-delete ${deleting?.dealerName}?`}
        confirmLabel="Delete"
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && deleteMut.mutate(deleting.id)}
      />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}>
        <Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}
